import { CustomerListMatchingAttribute } from '~/types/api.graphql';

import { parseMatchingField } from './schemas';
import { exceedsSubmissionValueLimit } from './submission';
import {
  CUSTOMER_LIST_MAPPING_KIND,
  CustomerListColumn,
  CustomerListColumnTypes,
  CustomerListMappingValue,
  CustomerListMatchingField,
  CustomerListMatchingLimits,
  CustomerListRuleDraft,
  MatchingRulesView,
} from './types';

/**
 * Why the mapping form can't be continued. Most mirror the API's checks of `customer-list-matching-rules` that need
 * only the form's values, under the API's own keys; the API stays the authority on creation.
 */
export enum MatchingRulesProblem {
  NoRules = 'noRules',
  EmptyRule = 'emptyMatchingRule',
  UntypedColumn = 'untypedColumn',
  TooManyRules = 'tooManyMatchingRules',
  TooManyFields = 'tooManyMatchingRuleFields',
  DuplicateAttributeInRule = 'duplicateMatchingAttributeInRule',
  DuplicateRule = 'duplicateMatchingRule',
  CollidingColumnNames = 'inconsistentMatchingColumnName',
  TooLongToSend = 'rulesTooLongToSend',
}

export type MatchingRulesIssue = {
  problem: MatchingRulesProblem;
  /** The rule it's about, when it's about one. */
  ruleIndex?: number;
  /** The columns it's about, when it's about some. */
  columnIndexes?: number[];
};

type MappingInput = {
  attachmentId: string;
  columns: CustomerListColumn[];
  types: CustomerListColumnTypes;
  rules: CustomerListRuleDraft[];
  limits: CustomerListMatchingLimits;
};

/** Column names are compared trimmed, with inner whitespace collapsed, and case-insensitively, as the API does. */
export const normalizeColumnName = (columnName: string) => columnName.trim().replace(/\s+/g, ' ').toLowerCase();

export const isIdentifierAttribute = (
  attribute: CustomerListMatchingAttribute | undefined,
  identifierAttributes: CustomerListMatchingAttribute[],
) => !!attribute && identifierAttributes.includes(attribute);

/** The simple view's rules: one rule per ticked column, in file order. */
export const buildSimpleRules = (columns: CustomerListColumn[], tickedColumns: number[]): CustomerListRuleDraft[] =>
  columns.filter(({ index }) => tickedColumns.includes(index)).map(({ index }) => [index]);

/** The columns a new form ticks: those typed as an identifier. */
export const findIdentifierColumns = (
  columns: CustomerListColumn[],
  types: CustomerListColumnTypes,
  identifierAttributes: CustomerListMatchingAttribute[],
) => columns.filter(({ index }) => isIdentifierAttribute(types[index], identifierAttributes)).map(({ index }) => index);

/** A rule without an identifier attribute, which matches a row only when few people share its values. */
export const isProfileOnlyRule = (
  rule: CustomerListRuleDraft,
  types: CustomerListColumnTypes,
  identifierAttributes: CustomerListMatchingAttribute[],
) => rule.length > 0 && !rule.some((index) => isIdentifierAttribute(types[index], identifierAttributes));

/**
 * The rules **Back to simple matching** would remove, and so asks about: every rule other than one identifier column.
 * An empty rule loses nothing, so it is removed without asking.
 */
export const findRulesDroppedBySimpleView = (
  rules: CustomerListRuleDraft[],
  types: CustomerListColumnTypes,
  identifierAttributes: CustomerListMatchingAttribute[],
) =>
  rules.flatMap((rule, ruleIndex) =>
    !rule.length || (rule.length === 1 && isIdentifierAttribute(types[rule[0]], identifierAttributes))
      ? []
      : [ruleIndex],
  );

/** Typed columns in no rule, in file order: a suggested type left unticked included. */
export const findUnusedColumns = (
  columns: CustomerListColumn[],
  types: CustomerListColumnTypes,
  rules: CustomerListRuleDraft[],
) => {
  const usedIndexes = new Set(rules.flat());
  return columns.filter(({ index }) => types[index] && !usedIndexes.has(index));
};

export const canAddRule = (rules: CustomerListRuleDraft[], limits: CustomerListMatchingLimits) =>
  rules.length < limits.maxRules;

/** The value the component submits; columns without a type are left out of every rule. */
export const buildMappingValue = ({ attachmentId, columns, types, rules, limits }: MappingInput) => {
  const toField = (index: number): CustomerListMatchingField[] => {
    const column = columns.find((candidate) => candidate.index === index);
    const attribute = types[index];
    return column && attribute ? [[index, column.columnName, attribute]] : [];
  };
  const value: CustomerListMappingValue = {
    kind: CUSTOMER_LIST_MAPPING_KIND,
    attachmentId,
    rules: rules.map((rule) => rule.flatMap(toField)),
    unused: findUnusedColumns(columns, types, rules).flatMap(({ index }) => toField(index)),
    identifierAttributes: limits.identifierAttributes,
  };
  return value;
};

/** Every reason the form can't be continued, empty when it can. */
export const findMatchingRulesIssues = (input: MappingInput): MatchingRulesIssue[] => {
  const { columns, types, rules, limits } = input;
  const issues: MatchingRulesIssue[] = [];
  const columnName = (index: number) => columns.find((column) => column.index === index)?.columnName ?? '';

  if (!rules.length) issues.push({ problem: MatchingRulesProblem.NoRules });
  if (rules.length > limits.maxRules) issues.push({ problem: MatchingRulesProblem.TooManyRules });

  const ruleKeys = new Set<string>();
  rules.forEach((rule, ruleIndex) => {
    if (!rule.length) issues.push({ problem: MatchingRulesProblem.EmptyRule, ruleIndex });
    if (rule.length > limits.maxFieldsPerRule) issues.push({ problem: MatchingRulesProblem.TooManyFields, ruleIndex });

    const untyped = rule.filter((index) => !types[index]);
    if (untyped.length) issues.push({ problem: MatchingRulesProblem.UntypedColumn, ruleIndex, columnIndexes: untyped });

    const attributes = rule.map((index) => types[index]).filter(Boolean);
    if (new Set(attributes).size !== attributes.length) {
      issues.push({ problem: MatchingRulesProblem.DuplicateAttributeInRule, ruleIndex });
    }

    // Same columns and types; columns whose names compare equal are their own issue below
    const ruleKey = JSON.stringify(rule.map((index) => `${index}:${types[index]}`).sort());
    if (rule.length && ruleKeys.has(ruleKey)) issues.push({ problem: MatchingRulesProblem.DuplicateRule, ruleIndex });
    ruleKeys.add(ruleKey);
  });

  const usedIndexes = [...new Set(rules.flat())];
  const collidingIndexes = usedIndexes.filter((index) =>
    usedIndexes.some(
      (other) => other !== index && normalizeColumnName(columnName(other)) === normalizeColumnName(columnName(index)),
    ),
  );
  if (collidingIndexes.length) {
    issues.push({ problem: MatchingRulesProblem.CollidingColumnNames, columnIndexes: collidingIndexes });
  }

  if (exceedsSubmissionValueLimit(JSON.stringify(buildMappingValue(input)))) {
    issues.push({ problem: MatchingRulesProblem.TooLongToSend });
  }
  return issues;
};

/**
 * The types and rules a form starts from: an earlier submitted value, or one the agent built from rules the user stated
 * before the form, either possibly carrying a rule the agent added. A field that isn't `[index, columnName, attribute]`
 * for an inspected column is left out with its whole rule, since a grouped rule missing a column isn't the rule that
 * was asked for. `unused` entries are read one by one. A value without them is one the agent built from stated rules,
 * so columns outside the rules start from their suggested types, unticked.
 */
export const readMappingValue = (value: { rules: unknown[]; unused?: unknown[] }, columns: CustomerListColumn[]) => {
  const readField = (field: unknown) => {
    const parsed = parseMatchingField(field);
    const isInspected = columns.some((column) => column.index === parsed?.[0] && column.columnName === parsed?.[1]);
    return isInspected ? parsed : null;
  };
  const rules = value.rules.flatMap((rule) => {
    const fields = Array.isArray(rule) ? rule.map(readField) : [null];
    return fields.every(Boolean) ? [fields] : [];
  });
  const unused = (value.unused ?? []).map(readField).filter(Boolean);
  const types: CustomerListColumnTypes = {};
  if (!value.unused) {
    columns.forEach(({ index, suggestedAttribute }) => {
      if (suggestedAttribute) types[index] = suggestedAttribute;
    });
  }
  [...rules.flat(), ...unused].forEach(([index, , attribute]) => {
    types[index] = attribute;
  });
  return { types, rules: rules.map((rule) => rule.map(([index]) => index)) };
};

/** The view a form opens in: the requested one, unless the simple view can't show its rules: a grouped or
 * profile-only rule. */
export const chooseInitialView = ({
  rules,
  types,
  identifierAttributes,
  requestedView,
}: {
  rules: CustomerListRuleDraft[];
  types: CustomerListColumnTypes;
  identifierAttributes: CustomerListMatchingAttribute[];
  requestedView?: MatchingRulesView;
}): MatchingRulesView =>
  findRulesDroppedBySimpleView(rules, types, identifierAttributes).length ? 'custom' : requestedView ?? 'simple';
